import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faTimes, faSearch, faDownload } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../AuthContext';
import { getPlatformIcon, getPlatformColor } from '../../platformIcons';
import Modal from '../../components/Modal';
import './AdminServices.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';

const defaultCategories = ['Followers', 'Likes', 'Comments', 'Views', 'Subscribers', 'Members', 'Plays', 'Shares'];

export default function AdminServices() {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState(defaultCategories);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ platform: '', category: '', name: '', rate: '', min_quantity: '', max_quantity: '', description: '', provider_id: '', provider_service_id: '' });
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [modal, setModal] = useState({ open: false, type: 'info', title: '', message: '' });
  const [confirmCb, setConfirmCb] = useState(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchServices = async () => {
    const res = await fetch(`${API}/admin/services`, { headers });
    const data = await res.json();
    if (Array.isArray(data)) setServices(data);
  };

  const fetchProviders = async () => {
    const res = await fetch(`${API}/admin/providers`, { headers });
    const data = await res.json();
    if (Array.isArray(data)) setProviders(data);
  };

  useEffect(() => {
    fetchServices();
    fetchProviders();
    fetch(`${API}/settings/metadata`)
      .then((r) => r.json())
      .then((d) => { if (d.categories) setCategories(d.categories); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = services.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.platform.toLowerCase().includes(search.toLowerCase())
  );

  const filteredIds = filtered.map((s) => s.id);

  const allSelected = filtered.length > 0 && selected.length === filtered.length;

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    setSelected((prev) => prev.length === filtered.length ? [] : [...filteredIds]);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ platform: '', category: '', name: '', rate: '', min_quantity: '', max_quantity: '', description: '', provider_id: '', provider_service_id: '' });
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditing(s.id);
    setForm({
      platform: s.platform,
      category: s.category,
      name: s.name,
      rate: s.rate.toString(),
      min_quantity: s.min_quantity.toString(),
      max_quantity: s.max_quantity.toString(),
      description: s.description || '',
      provider_id: s.provider_id?.toString() || '',
      provider_service_id: s.provider_service_id || '',
    });
    setShowModal(true);
  };

  const handleFetchService = async () => {
    if (!form.provider_id || !form.provider_service_id) {
      setModal({ open: true, type: 'error', title: 'Missing Info', message: 'Select a provider and enter a service ID.' });
      return;
    }
    setFetching(true);
    try {
      const res = await fetch(`${API}/admin/providers/${form.provider_id}/lookup-service`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ provider_service_id: form.provider_service_id }),
      });
      const data = await res.json();
      if (data.error) {
        setModal({ open: true, type: 'error', title: 'Fetch Failed', message: data.error });
        return;
      }
      setForm((prev) => ({
        ...prev,
        name: data.name || prev.name,
        rate: data.rate?.toString() || prev.rate,
        min_quantity: data.min?.toString() || prev.min_quantity,
        max_quantity: data.max?.toString() || prev.max_quantity,
        category: data.category || prev.category,
        platform: data.category || prev.platform,
      }));
    } catch { /* ignore */ } finally {
      setFetching(false);
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.platform || !form.category || !form.rate) return;
    setSaving(true);
    const body = {
      ...form,
      rate: parseFloat(form.rate),
      min_quantity: parseInt(form.min_quantity),
      max_quantity: parseInt(form.max_quantity),
      provider_id: form.provider_id ? parseInt(form.provider_id) : null,
    };
    const url = editing ? `${API}/admin/services/${editing}` : `${API}/admin/services`;
    const method = editing ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
    setSaving(false);
    if (!res.ok) {
      const data = await res.json();
      setModal({ open: true, type: 'error', title: 'Save Failed', message: data.message || 'Could not save service.' });
      return;
    }
    setShowModal(false);
    fetchServices();
  };

  const handleDelete = (id) => {
    setConfirmCb(() => async () => {
      await fetch(`${API}/admin/services/${id}`, { method: 'DELETE', headers });
      fetchServices();
    });
    setModal({ open: true, type: 'confirm', title: 'Delete Service', message: 'Are you sure you want to delete this service?' });
  };

  const handleBulkDelete = () => {
    setConfirmCb(() => async () => {
      const res = await fetch(`${API}/admin/services/bulk-delete`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ids: selected }),
      });
      const data = await res.json();
      setModal({ open: true, type: data.error ? 'error' : 'success', title: 'Bulk Delete', message: data.message || 'Done' });
      setSelected([]);
      fetchServices();
    });
    setModal({ open: true, type: 'confirm', title: 'Delete Services', message: `Delete ${selected.length} selected service(s)?` });
  };

  return (
    <div className="admin-services">
      <div className="admin-page-header">
        {selected.length > 0 ? (
          <button className="admin-bulk-delete-btn" onClick={handleBulkDelete}>
            <FontAwesomeIcon icon={faTrash} /> Delete Selected ({selected.length})
          </button>
        ) : (
          <span />
        )}
        <button className="admin-add-btn" onClick={openAdd}>
          <FontAwesomeIcon icon={faPlus} /> Add Service
        </button>
      </div>

      <div className="search-bar admin-search">
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <input type="text" className="search-input" placeholder="Search services..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="admin-table-card">
        <table className="admin-services-table">
          <thead>
            <tr>
              <th className="select-cell">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              </th>
              <th>ID</th>
              <th>Service</th>
              <th>Platform</th>
              <th>Category</th>
              <th>Rate (TSHS)</th>
              <th>Min</th>
              <th>Max</th>
              <th>Provider</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} className={selected.includes(s.id) ? 'row-selected' : ''}>
                <td className="select-cell">
                  <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggleSelect(s.id)} />
                </td>
                <td className="id-cell">{s.id}</td>
                <td className="name-cell">{s.name}</td>
                <td>
                  <span className="platform-badge">
                    <FontAwesomeIcon icon={getPlatformIcon(s.platform)} style={{ marginRight: 6, color: getPlatformColor(s.platform) }} />
                    {s.platform}
                  </span>
                </td>
                <td><span className="cat-badge">{s.category}</span></td>
                <td className="rate-cell">TSHS {Number(s.rate).toLocaleString()}</td>
                <td>{Number(s.min_quantity).toLocaleString()}</td>
                <td>{Number(s.max_quantity).toLocaleString()}</td>
                <td className="provider-cell">{s.provider?.name || '—'}</td>
                <td>
                  <div className="action-btns">
                    <button className="action-btn edit" onClick={() => openEdit(s)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button className="action-btn delete" onClick={() => handleDelete(s.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Service' : 'Add Service'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Provider</label>
                  <select className="form-input" value={form.provider_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })}>
                    <option value="">None</option>
                    {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Provider Service ID</label>
                  <div className="fetch-row">
                    <input type="text" className="form-input" value={form.provider_service_id} onChange={(e) => setForm({ ...form, provider_service_id: e.target.value })} placeholder="e.g. 123" />
                    <button type="button" className="fetch-btn" onClick={handleFetchService} disabled={fetching}>
                      <FontAwesomeIcon icon={faDownload} /> {fetching ? 'Fetching...' : 'Fetch'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Platform</label>
                <input type="text" className="form-input" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} placeholder="e.g. Instagram, Snapchat" />
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                  <select className="form-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option value="">Select category</option>
                    <option value="Other">Other</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <p className="form-hint">Manage categories in Settings</p>
              </div>
              <div className="form-group">
                <label className="form-label">Service Name</label>
                <input type="text" className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Rate (TSHS per 1000)</label>
                  <input type="number" className="form-input" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Min Quantity</label>
                  <input type="number" className="form-input" value={form.min_quantity} onChange={(e) => setForm({ ...form, min_quantity: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Max Quantity</label>
                  <input type="number" className="form-input" value={form.max_quantity} onChange={(e) => setForm({ ...form, max_quantity: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input type="text" className="form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="save-btn" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={modal.open}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onClose={() => { setModal({ ...modal, open: false }); setConfirmCb(null); }}
        onConfirm={() => { setModal({ ...modal, open: false }); setTimeout(() => confirmCb?.(), 100); }}
        confirmLabel={selected.length > 0 ? 'Delete All' : 'Delete'}
      />
    </div>
  );
}
